import React from "react";
import { motion } from "framer-motion";
import {
  Calendar,
  Users,
  FileText,
  MessageCircle,
  TrendingUp,
  Sun,
} from "lucide-react";

const steps = [
  {
    id: 1,
    title: "Book Your Consultation",
    description:
      "Choose a time that suits you through our convenient calendar app. It’s quick, seamless, and instantly confirms your appointment. You’ll also receive a short pre-assessment form to help us understand your goals better.",
    icon: Calendar,
  },
  {
    id: 2,
    title: "In-Depth First Session",
    description:
      "We begin with a warm, detailed conversation about your lifestyle, eating habits, medical history, challenges, and expectations. This helps us understand you holistically—beyond just numbers and reports.",
    icon: Users,
  },
  {
    id: 3,
    title: "Personalised Wellness Plan",
    description:
      "After your assessment, we design a customised nutrition and lifestyle plan tailored to your routine, preferences, and specific health needs. Every recommendation is practical, supportive, and easy to follow.",
    icon: FileText,
  },
  {
    id: 4,
    title: "Implementation & Support",
    description:
      "As you begin your wellness journey, we offer comprehensive ongoing support. This includes daily WhatsApp reminders (recalls), answers to your queries, and timely guidance to help you stay consistent and confident as you integrate the plan into your daily life.",
    icon: MessageCircle,
  },
  {
    id: 5,
    title: "Progress Check-Ins",
    description:
      "Regular follow-up sessions allow us to track your progress, refine your plan, and keep you motivated. Your plan evolves with you, ensuring it stays effective and sustainable.",
    icon: TrendingUp,
  },
  {
    id: 6,
    title: "Long-Term Wellness",
    description:
      "By the end of your program, you’ll have a clear, personalised roadmap to maintain your results. And even after your program ends, we’re always just a message away whenever you need support.",
    icon: Sun,
  },
];

const ProcessTimeline = () => {
  return (
    <section id="journey" className="py-32 bg-secondary/10">
      <div className="container mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold tracking-[0.2em] text-muted-foreground uppercase mb-4 block">
              How It Works
            </span>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-6">
              Your guided wellness path
            </h2>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 relative">
          {/* Connecting line for desktop */}
          <div className="hidden lg:block absolute top-12 left-0 w-full h-0.5 bg-border/50 -z-10" />

          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              className="relative bg-white/40 backdrop-blur-md p-8 rounded-2xl border border-white/20 shadow-sm hover:shadow-lg hover:bg-white/60 transition-all duration-300"
            >
              <div className="w-12 h-12 bg-white/50 rounded-full flex items-center justify-center mb-6 text-primary mx-auto lg:mx-0 shadow-inner border border-white/20">
                <step.icon size={24} />
              </div>
              <div className="absolute top-8 right-8 text-6xl font-serif font-bold text-muted/20 select-none">
                {step.id}
              </div>
              <h3 className="text-xl font-serif font-semibold mb-3 text-foreground">
                {step.title}
              </h3>
              <p className="text-muted-foreground leading-relaxed text-sm">
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
